/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-22
 */

import { useState, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  LinearProgress,
  Zoom,
  Backdrop,
} from '@mui/material';
import { Send as SendIcon, CheckCircle, Error } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSubmitContactFormMutation } from '../../../features/contact';
import TurnstileWidget from '../Waitlist/waitlist/TurnstileWidget';
import ErrorAlert from '../../common/ErrorAlert';
import { categorizeError } from '../../../utils/errorHandling';

// Form validation interface
interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
  turnstile?: string;
}

// Form data interface
interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

/**
 * Contact form component with validation and Turnstile protection.
 * @example
 * return (
 *   <ContactForm />
 * )
 * @returns {React.ReactElement} component.
 */
const ContactForm: React.FC = () => {
  const { t } = useTranslation('contact');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [apiErrorType, setApiErrorType] = useState<
    'network' | 'server' | 'client' | 'ratelimit' | 'validation' | 'unknown'
  >('unknown');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldValidationStates, setFieldValidationStates] = useState<{
    name: 'idle' | 'valid' | 'invalid';
    email: 'idle' | 'valid' | 'invalid';
    subject: 'idle' | 'valid' | 'invalid';
    message: 'idle' | 'valid' | 'invalid';
  }>({
    name: 'idle',
    email: 'idle',
    subject: 'idle',
    message: 'idle',
  });
  const [submitContactForm, { isLoading }] = useSubmitContactFormMutation();

  // Validation functions
  const validateName = (name: string): string | undefined => {
    const trimmed = name.trim();
    if (!trimmed) return t('form.validation.nameRequired');
    if (trimmed.length < 2) return t('form.validation.nameMinLength');
    if (trimmed.length > 255) return t('form.validation.nameMaxLength');
    return undefined;
  };

  const validateEmail = (email: string): string | undefined => {
    const trimmed = email.trim();
    if (!trimmed) return t('form.validation.emailRequired');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) return t('form.validation.emailInvalid');
    return undefined;
  };

  const validateSubject = (subject: string): string | undefined => {
    const trimmed = subject.trim();
    if (!trimmed) return t('form.validation.subjectRequired');
    if (trimmed.length < 3) return t('form.validation.subjectMinLength');
    if (trimmed.length > 255) return t('form.validation.subjectMaxLength');
    return undefined;
  };

  const validateMessage = (message: string): string | undefined => {
    const trimmed = message.trim();
    if (!trimmed) return t('form.validation.messageRequired');
    if (trimmed.length < 10) return t('form.validation.messageMinLength');
    if (trimmed.length > 5000) return t('form.validation.messageMaxLength');
    return undefined;
  };

  // Clear API errors when user starts typing
  const clearApiErrorsOnChange = useCallback(() => {
    if (apiError) {
      setApiError(null);
      setApiErrorType('unknown');
    }
  }, [apiError]);

  /**
   * Gets the validation icon for a field based on its state.
   */
  const getValidationIcon = (fieldName: keyof FormData) => {
    const state = fieldValidationStates[fieldName];
    const hasError = !!errors[fieldName];

    if (state === 'idle') return null;

    if (hasError) {
      return (
        <Zoom in>
          <Error color="error" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    if (state === 'valid') {
      return (
        <Zoom in>
          <CheckCircle color="success" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    return null;
  };

  // Handle input changes with validation
  const handleInputChange =
    (field: keyof FormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;
      setFormData(prev => ({ ...prev, [field]: value }));

      // Clear field error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: undefined }));
      }

      // Update field validation states for visual feedback
      const hasValue = value.trim().length > 0;
      let validationState: 'idle' | 'valid' | 'invalid' = 'idle';

      if (hasValue) {
        // Check for validation errors
        let fieldError: string | undefined;
        switch (field) {
          case 'name':
            fieldError = validateName(value);
            break;
          case 'email':
            fieldError = validateEmail(value);
            break;
          case 'subject':
            fieldError = validateSubject(value);
            break;
          case 'message':
            fieldError = validateMessage(value);
            break;
          default:
            fieldError = undefined;
            break;
        }

        validationState = fieldError ? 'invalid' : 'valid';
      }

      setFieldValidationStates(prev => ({
        ...prev,
        [field]: validationState,
      }));

      // Clear API errors
      clearApiErrorsOnChange();
    };

  // Validate entire form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    newErrors.name = validateName(formData.name);
    newErrors.email = validateEmail(formData.email);
    newErrors.subject = validateSubject(formData.subject);
    newErrors.message = validateMessage(formData.message);

    if (!turnstileToken) {
      newErrors.turnstile = t('form.validation.turnstileRequired');
    }

    setErrors(newErrors);

    return !Object.values(newErrors).some(error => error !== undefined);
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    // Clear previous API errors
    setApiError(null);
    setApiErrorType('unknown');

    try {
      const result = await submitContactForm({
        ...formData,
        turnstile_token: turnstileToken,
      }).unwrap();

      if (result.success) {
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTurnstileToken('');
        setErrors({});
        setApiError(null);
        setFieldValidationStates({
          name: 'idle',
          email: 'idle',
          subject: 'idle',
          message: 'idle',
        });
      }
    } catch (error: any) {
      // Use enhanced error categorization
      const categorizedError = categorizeError(error);

      // Set API error for display in ErrorAlert
      setApiError(categorizedError.message);
      setApiErrorType(categorizedError.type);

      // Handle Turnstile-specific errors in the existing error area
      if (
        categorizedError.type === 'validation' &&
        (error.data?.error?.toLowerCase().includes('turnstile') ||
          error.data?.error?.toLowerCase().includes('captcha'))
      ) {
        setErrors({ turnstile: categorizedError.message });
        // Don't show in main error alert for Turnstile errors
        setApiError(null);
      } else if (categorizedError.type === 'validation') {
        // Other validation errors can be shown in the main error area
        setErrors({ turnstile: categorizedError.message });
        // Don't show in main error alert for validation errors
        setApiError(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle retry functionality
  const handleRetry = () => {
    setApiError(null);
    setApiErrorType('unknown');
    // Trigger form submission by creating a synthetic event
    const syntheticEvent = {
      preventDefault: () => {},
    } as React.FormEvent;
    handleSubmit(syntheticEvent);
  };

  // Handle Turnstile success
  const handleTurnstileSuccess = (token: string) => {
    setTurnstileToken(token);
    if (errors.turnstile) {
      setErrors(prev => ({ ...prev, turnstile: undefined }));
    }
  };

  // Handle Turnstile error
  const handleTurnstileError = () => {
    setTurnstileToken('');
    setErrors(prev => ({
      ...prev,
      turnstile: t('form.validation.turnstileFailed'),
    }));
  };

  // Handle Turnstile expiry
  const handleTurnstileExpire = () => {
    setTurnstileToken('');
    setErrors(prev => ({
      ...prev,
      turnstile: t('form.validation.turnstileExpired'),
    }));
  };

  // Calculate message character count
  const messageCharCount = formData.message.length;
  const messageCharLimit = 5000;

  // Check if form is valid for submission
  const isFormValid =
    !Object.values(errors).some(error => error !== undefined) &&
    formData.name.trim() &&
    formData.email.trim() &&
    formData.subject.trim() &&
    formData.message.trim() &&
    turnstileToken;

  if (submitSuccess) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontWeight: 600, color: 'primary.main' }}
        >
          {t('form.success.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('form.success.message')}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => setSubmitSuccess(false)}
          sx={{ textTransform: 'none' }}
        >
          {t('form.success.sendAnother')}
        </Button>
      </Paper>
    );
  }

  return (
    <>
      {/* Loading backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: theme => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
        open={isLoading && isSubmitting}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          {t('form.submitting')}
        </Typography>
      </Backdrop>

      {/* Progress bar */}
      {(isLoading || isSubmitting) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: theme => theme.zIndex.appBar + 1,
          }}
        />
      )}

      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
          {t('form.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('form.description')}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 3,
            }}
          >
            <TextField
              fullWidth
              label={t('form.labels.name')}
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              required
              disabled={isLoading || isSubmitting}
              InputProps={{
                endAdornment: getValidationIcon('name'),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
            <TextField
              fullWidth
              label={t('form.labels.email')}
              type="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
              disabled={isLoading || isSubmitting}
              InputProps={{
                endAdornment: getValidationIcon('email'),
              }}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
          </Box>

          <TextField
            fullWidth
            label={t('form.labels.subject')}
            value={formData.subject}
            onChange={handleInputChange('subject')}
            error={!!errors.subject}
            helperText={errors.subject}
            required
            disabled={isLoading || isSubmitting}
            InputProps={{
              endAdornment: getValidationIcon('subject'),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: '2px',
                },
                '&.Mui-focused fieldset': {
                  borderWidth: '2px',
                },
                transition: 'all 0.2s ease-in-out',
              },
            }}
          />

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={t('form.labels.message')}
              multiline
              rows={6}
              value={formData.message}
              onChange={handleInputChange('message')}
              error={!!errors.message}
              helperText={
                errors.message ||
                t('form.characterCount', {
                  count: messageCharCount,
                  limit: messageCharLimit,
                })
              }
              required
              disabled={isLoading || isSubmitting}
              InputProps={{
                endAdornment: getValidationIcon('message'),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused fieldset': {
                    borderWidth: '2px',
                  },
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            />
            <LinearProgress
              variant="determinate"
              value={(messageCharCount / messageCharLimit) * 100}
              sx={{
                mt: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  backgroundColor:
                    messageCharCount > messageCharLimit
                      ? 'error.main'
                      : 'primary.main',
                },
              }}
            />
          </Box>

          <TurnstileWidget
            onSuccess={handleTurnstileSuccess}
            onError={handleTurnstileError}
            onExpire={handleTurnstileExpire}
            disabled={isLoading}
          />

          {errors.turnstile && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.turnstile}
            </Alert>
          )}

          {/* Enhanced error display for API errors */}
          <ErrorAlert
            error={apiError}
            errorType={apiErrorType}
            onRetry={handleRetry}
            showRetry={
              apiError !== null &&
              ['network', 'server', 'ratelimit'].includes(apiErrorType)
            }
            onDismiss={() => setApiError(null)}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={!isFormValid || isLoading || isSubmitting}
            startIcon={
              isLoading || isSubmitting ? (
                <CircularProgress size={20} />
              ) : (
                <SendIcon />
              )
            }
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: 3,
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {isLoading || isSubmitting
              ? t('form.submitting')
              : t('form.submit')}
          </Button>
        </Box>
      </Paper>
    </>
  );
};

export default ContactForm;
